import React, { useState, useEffect } from "react";
import { Button, Card, IconButton, TextField, CircularProgress } from "@material-ui/core";
import { CloudUpload, AddPhotoAlternate, Brush } from "@material-ui/icons";

interface MediaFieldProps {
  title?: string;
  value?: string;
  onValueChange: (value: string) => void;
  onUploadClicked: () => void;
  onBrowseClicked?: () => void;
  onGeneratorClicked?: () => void;
  loading?: boolean;
  children?: React.ReactNode;
  previewContent?: React.ReactNode;
  browseIcon?: React.ReactNode;
}

const MediaField: React.FC<MediaFieldProps> = ({
  title,
  value,
  onValueChange,
  onUploadClicked,
  onBrowseClicked,
  onGeneratorClicked,
  loading = false,
  children,
  previewContent,
  browseIcon = <AddPhotoAlternate />,
}) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [imageKey, setImageKey] = useState(0);

  // Reset loading state when value changes
  useEffect(() => {
    if (value) {
      setImageLoading(true);
      setRetryCount(0);
      setImageKey(prev => prev + 1);
    }
  }, [value]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setRetryCount(0);
  };

  const handleImageError = () => {
    const maxRetries = 4; // Will try 5 times total (initial + 4 retries)

    if (retryCount < maxRetries) {
      // Exponential backoff: 1s, 2s, 4s, 8s
      const delay = Math.pow(2, retryCount) * 1000;

      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImageKey(prev => prev + 1); // Force img reload
      }, delay);
    } else {
      // Max retries reached, stop loading and show broken image
      setImageLoading(false);
    }
  };

  return (
    <Card elevation={2} style={{ padding: "16px" }}>
      {value && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, position: "relative" }}>
          {previewContent || (
            <>
              {imageLoading && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </div>
              )}
              <img
                key={imageKey}
                src={value}
                alt="Selected"
                style={{
                  maxHeight: 480,
                  maxWidth: "100%",
                  borderRadius: 4,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  opacity: imageLoading ? 0 : 1,
                  transition: "opacity 0.3s",
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            </>
          )}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "flex-end", gap: "16px" }}>
        <TextField
          label={title}
          value={value || ""}
          fullWidth
          style={{ flex: 1 }}
          onChange={e => onValueChange(e.target.value)}
          disabled={loading}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
          <Button
            variant="text"
            color="primary"
            startIcon={<CloudUpload />}
            disabled={loading}
            onClick={onUploadClicked}
          />

          {onBrowseClicked && (
            <IconButton disabled={loading} color="primary" onClick={onBrowseClicked}>
              {browseIcon}
            </IconButton>
          )}

          {onGeneratorClicked && (
            <IconButton disabled={loading} color="primary" onClick={onGeneratorClicked}>
              <Brush />
            </IconButton>
          )}
        </div>
      </div>
      {children}
    </Card>
  );
};

export default MediaField;
